import { useCallback, useEffect, useRef, useState } from 'react';

import { useLatest, useSingleFlight } from '@videa/utils';

export type RecorderStatus =
  | 'idle'
  | 'acquiring_media'
  | 'recording'
  | 'paused'
  | 'stopped'
  | 'permission_denied'
  | 'recorder_error';

export interface UseMediaRecorderOptions {
  /** Audio constraints or device ID */
  audio?: boolean | MediaTrackConstraints;
  /** Chunk interval in ms (default: 30000 = 30 seconds) */
  timeslice?: number;
  /** MIME type for recording (default: audio/webm) */
  mimeType?: string;
  /** Called when a new chunk is available */
  onChunkAvailable?: (chunk: Blob, chunkIndex: number) => void;
  /** Called when recording stops with final blob */
  onRecordingComplete?: (blob: Blob) => void;
  /** Called on error */
  onError?: (error: Error) => void;
}

export interface UseMediaRecorderReturn {
  /** Current recorder status */
  status: RecorderStatus;
  /** URL to the recorded blob (available after stop) */
  mediaBlobUrl: string | undefined;
  /** The preview audio stream (for visualizations) */
  previewStream: MediaStream | null;
  /** Start recording */
  startRecording: () => Promise<void>;
  /** Stop recording */
  stopRecording: () => void;
  /** Pause recording */
  pauseRecording: () => void;
  /** Resume recording */
  resumeRecording: () => void;
  /** Clear the recorded blob URL */
  clearBlobUrl: () => void;
  /** Error message if any */
  error: string | null;
}

/** Get the best supported MIME type for audio recording */
const getBestSupportedMimeType = (): string => {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
    'audio/wav',
  ];

  for (const type of types) {
    if (
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported(type)
    ) {
      return type;
    }
  }

  return 'audio/webm';
};

/**
 * Custom hook for audio recording with native MediaRecorder API.
 * Provides streaming chunks for OPFS persistence and full control over recording.
 */
export const useMediaRecorder = (
  options: UseMediaRecorderOptions = {},
): UseMediaRecorderReturn => {
  const {
    audio = true,
    timeslice = 30000,
    mimeType = getBestSupportedMimeType(),
    onChunkAvailable,
    onRecordingComplete,
    onError,
  } = options;

  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [mediaBlobUrl, setMediaBlobUrl] = useState<string | undefined>();
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const chunkIndexRef = useRef(0);
  const mediaBlobUrlRef = useRef<string | undefined>(); // Track blob URL for cleanup
  const abortControllerRef = useRef<AbortController | null>(null); // For cleanup during unmount

  // Use useLatest to avoid stale closures in MediaRecorder event handlers
  const onChunkAvailableRef = useLatest(onChunkAvailable);
  const onRecordingCompleteRef = useLatest(onRecordingComplete);
  const onErrorRef = useLatest(onError);

  // Cleanup function
  const cleanup = useCallback(() => {
    // Abort any pending getUserMedia or async operations
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setPreviewStream(null);
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    chunkIndexRef.current = 0;
  }, []);

  // Handle chunk available - uses ref to always get latest callback
  const handleDataAvailable = useCallback((event: BlobEvent) => {
    if (event.data.size > 0) {
      chunksRef.current.push(event.data);
      onChunkAvailableRef.current?.(event.data, chunkIndexRef.current);
      chunkIndexRef.current++;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, []);

  // Handle recording stop - uses ref to always get latest callback
  const handleStop = useCallback(() => {
    if (chunksRef.current.length > 0) {
      const blob = new Blob(chunksRef.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      mediaBlobUrlRef.current = url;
      setMediaBlobUrl(url);
      onRecordingCompleteRef.current?.(blob);
    }
    setStatus('stopped');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
  }, [mimeType]);

  // Handle errors - uses ref to always get latest callback
  const handleError = useCallback(
    (event: Event) => {
      // MediaRecorder.onerror provides error via event.error (DOMException), not event.message
      const mediaError = (event as Event & { error?: DOMException }).error;
      const errorMessage = mediaError?.message || 'Recording error';
      setError(errorMessage);
      setStatus('recorder_error');
      onErrorRef.current?.(mediaError || new Error(errorMessage));
      cleanup();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable
    [cleanup],
  );

  // Start recording implementation
  const startRecordingImpl = useCallback(async () => {
    // Domain-specific guard: prevent starting when already recording
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      return;
    }

    // Create abort controller for this recording session
    // Abort any previous pending operation
    abortControllerRef.current?.abort();
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    try {
      setStatus('acquiring_media');
      setError(null);
      chunksRef.current = [];
      chunkIndexRef.current = 0;

      // Revoke previous blob URL if exists
      if (mediaBlobUrlRef.current) {
        URL.revokeObjectURL(mediaBlobUrlRef.current);
        mediaBlobUrlRef.current = undefined;
        setMediaBlobUrl(undefined);
      }

      // Get media stream
      const constraints: MediaStreamConstraints = {
        audio: audio,
        video: false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      // Check if we were aborted while waiting for getUserMedia
      // (e.g., component unmounted or another recording started)
      if (abortController.signal.aborted) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      setPreviewStream(stream);

      // Create MediaRecorder
      const recorder = new MediaRecorder(stream, { mimeType });
      // Safe: useSingleFlight prevents concurrent calls, abort check ensures we haven't been cancelled
      // eslint-disable-next-line require-atomic-updates
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = handleDataAvailable;
      recorder.onstop = handleStop;
      recorder.onerror = handleError;

      // Start with timeslice for streaming chunks
      recorder.start(timeslice);
      setStatus('recording');
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to start recording';
      if (
        errorMessage.includes('Permission denied') ||
        errorMessage.includes('NotAllowed')
      ) {
        setStatus('permission_denied');
      } else {
        setStatus('recorder_error');
      }

      setError(errorMessage);
      onErrorRef.current?.(
        err instanceof Error ? err : new Error(errorMessage),
      );
      cleanup();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useLatest ref is stable, mediaBlobUrlRef is stable
  }, [
    audio,
    mimeType,
    timeslice,
    handleDataAvailable,
    handleStop,
    handleError,
    cleanup,
  ]);

  // Wrap with single-flight to prevent rapid double-calls
  const { run: startRecording } = useSingleFlight(startRecordingImpl);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state !== 'inactive'
    ) {
      mediaRecorderRef.current.stop();

      // Stop all tracks
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    }
  }, []);

  // Pause recording
  const pauseRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'recording'
    ) {
      mediaRecorderRef.current.pause();
      setStatus('paused');
    }
  }, []);

  // Resume recording
  const resumeRecording = useCallback(() => {
    if (
      mediaRecorderRef.current &&
      mediaRecorderRef.current.state === 'paused'
    ) {
      mediaRecorderRef.current.resume();
      setStatus('recording');
    }
  }, []);

  // Clear blob URL and reset to idle state
  const clearBlobUrl = useCallback(() => {
    if (mediaBlobUrlRef.current) {
      URL.revokeObjectURL(mediaBlobUrlRef.current);
      mediaBlobUrlRef.current = undefined;
      setMediaBlobUrl(undefined);
    }
    setStatus('idle');
  }, []);

  // Cleanup on unmount - use ref to ensure we always revoke the latest blob URL
  useEffect(() => {
    return () => {
      cleanup();
      if (mediaBlobUrlRef.current) {
        URL.revokeObjectURL(mediaBlobUrlRef.current);
      }
    };
  }, [cleanup]);

  return {
    status,
    mediaBlobUrl,
    previewStream,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearBlobUrl,
    error,
  };
};
